// Block class-name helpers.
//
// Core gives every block a `wp-block-<slug>` class. This plugin's blocks drop
// that prefix, so anything that needs to match a block by class - front-end
// view selectors, editor bundles, stylesheets - has to make the same
// calculation. Both directions live here so they cannot drift apart.

const BLOCK_CLASS_PREFIX = 'wp-block-';
const PLUGIN_BLOCK_NAMESPACE = 'amplify-storytelling/';

/**
 * Whether a block name belongs to this plugin.
 *
 * @param {string} blockName Block name, e.g. `amplify-storytelling/color-shift`.
 * @return {boolean} True when the block is one of ours.
 */
export const isPluginBlock = (blockName) =>
  typeof blockName === 'string' && blockName.startsWith(PLUGIN_BLOCK_NAMESPACE);

/**
 * Filter callback for `blocks.getBlockDefaultClassName`.
 *
 * Strips the `wp-block-` prefix from this plugin's blocks, leaving blocks from
 * anywhere else untouched. Register it with:
 *
 * ```js
 * addFilter( 'blocks.getBlockDefaultClassName', 'amplify-storytelling/remove-block-prefix', removeBlockClassPrefix );
 * ```
 *
 * @param {string} className The class name core generated, e.g. `wp-block-amplify-storytelling-color-shift`.
 * @param {string} blockName The block name being processed, e.g. `amplify-storytelling/color-shift`.
 * @return {string} The class name to use.
 */
export const removeBlockClassPrefix = (className, blockName) => {
  if (!isPluginBlock(blockName) || !className.startsWith(BLOCK_CLASS_PREFIX)) {
    return className;
  }

  return className.slice(BLOCK_CLASS_PREFIX.length);
};

/**
 * The class name a block's markup ends up with.
 *
 * Mirrors what core generates (`wp-block-` plus the slugified block name) and
 * then applies our own prefix stripping, so it always matches the class in the
 * editor and in saved markup. e.g. `amplify-storytelling/color-shift` becomes
 * `amplify-storytelling-color-shift`.
 *
 * @param {string} blockName Block name, e.g. `amplify-storytelling/color-shift`.
 * @return {string} The block's class name.
 */
export const blockNameToClassName = (blockName) =>
  removeBlockClassPrefix(
    `${BLOCK_CLASS_PREFIX}${blockName.replace('/', '-')}`,
    blockName
  );
